import React, { useRef, useState } from 'react';
import { Upload, X, MapPin, Car, Phone, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { BRAND_SWATCHES } from '../lib/brandSwatches';
import Logo from './marketing/Logo';
import toast from 'react-hot-toast';

interface BrandingStepProps {
  lotName: string;
  logoUrl: string;
  brandColor: string;
  onLogoChange: (url: string) => void;
  onColorChange: (color: string) => void;
  /** When true, file upload is available (admin/authenticated context) */
  allowUpload?: boolean;
  /** Lot ID for namespacing uploads in storage */
  lotId?: string;
}

export default function BrandingStep({
  lotName,
  logoUrl,
  brandColor,
  onLogoChange,
  onColorChange,
  allowUpload = false,
  lotId,
}: BrandingStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(logoUrl);
  const [urlMode, setUrlMode] = useState(!allowUpload);

  const accentColor = brandColor || '#EC4899';
  const displayName = lotName || 'Your Lot';

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2 MB');
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const folder = lotId ?? 'pending';
      const path = `${folder}/${Date.now()}.${ext}`;

      const { error } = await supabase.storage
        .from('lot-logos')
        .upload(path, file, { upsert: true });

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('lot-logos')
        .getPublicUrl(path);

      onLogoChange(urlData.publicUrl);
      setUrlInput(urlData.publicUrl);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error('Upload failed. Try pasting a URL instead.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUrlCommit = () => {
    const trimmed = urlInput.trim();
    if (trimmed && !/^https?:\/\//.test(trimmed)) {
      toast.error('URL must start with http:// or https://');
      return;
    }
    onLogoChange(trimmed);
  };

  return (
    <div className="space-y-6">
      {/* Logo section */}
      <div>
        <p className="text-sm font-semibold text-ink-700 mb-3">Logo</p>

        {allowUpload && (
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setUrlMode(false)}
              className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors ${
                !urlMode ? 'bg-ink-900 text-white' : 'bg-cream-100 text-ink-500 hover:bg-cream-200'
              }`}
            >
              Upload file
            </button>
            <button
              type="button"
              onClick={() => setUrlMode(true)}
              className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors ${
                urlMode ? 'bg-ink-900 text-white' : 'bg-cream-100 text-ink-500 hover:bg-cream-200'
              }`}
            >
              Paste URL
            </button>
          </div>
        )}

        {!urlMode && allowUpload ? (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              className="hidden"
              onChange={handleFileUpload}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-ink-300 rounded-xl py-6 hover:border-ink-500 transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <span className="h-5 w-5 border-2 border-ink-400 border-t-ink-700 rounded-full animate-spin" />
              ) : (
                <Upload className="h-6 w-6 text-ink-400" />
              )}
              <span className="text-sm text-ink-500">
                {uploading ? 'Uploading…' : 'Click to upload PNG, JPG, SVG, or WebP (max 2 MB)'}
              </span>
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onBlur={handleUrlCommit}
              onKeyDown={(e) => e.key === 'Enter' && handleUrlCommit()}
              placeholder="https://yourcompany.com/logo.png"
              className="flex-1 px-4 py-3 bg-white border border-ink-300 rounded-xl text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-confetti-pink focus:border-confetti-pink transition-all text-sm"
            />
            {logoUrl && (
              <button
                type="button"
                onClick={() => { onLogoChange(''); setUrlInput(''); }}
                className="p-3 rounded-xl border border-ink-300 text-ink-400 hover:text-ink-700 hover:border-ink-500 transition-colors"
                aria-label="Remove logo"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {logoUrl && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-cream-100 rounded-xl">
            <img
              src={logoUrl}
              alt="Logo preview"
              className="h-10 w-auto max-w-[120px] object-contain rounded"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="text-xs text-ink-500">Logo set</span>
          </div>
        )}

        <p className="mt-2 text-xs text-ink-400">
          Optional. If not set, the ParkConfetti logo will be shown. Recommended: square or landscape, at least 120 px tall.
        </p>
      </div>

      {/* Color swatches */}
      <div>
        <p className="text-sm font-semibold text-ink-700 mb-3">Brand Color</p>
        <div className="grid grid-cols-6 gap-2">
          {BRAND_SWATCHES.map((swatch) => {
            const selected = brandColor === swatch.value;
            return (
              <button
                key={swatch.value}
                type="button"
                title={swatch.label}
                onClick={() => onColorChange(swatch.value)}
                className="group relative flex flex-col items-center gap-1"
              >
                <span
                  className={`block h-9 w-9 rounded-xl transition-all ${
                    selected ? 'ring-2 ring-offset-2 ring-ink-900 scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: swatch.value }}
                />
                {selected && (
                  <span className="absolute top-0.5 left-0.5 right-0.5 bottom-0.5 flex items-center justify-center pointer-events-none">
                    <Check className="h-3.5 w-3.5 text-white drop-shadow" />
                  </span>
                )}
                <span className="text-[10px] text-ink-400 group-hover:text-ink-700 transition-colors truncate max-w-[40px]">
                  {swatch.label}
                </span>
              </button>
            );
          })}
        </div>
        {brandColor && (
          <button
            type="button"
            onClick={() => onColorChange('')}
            className="mt-2 text-xs text-ink-400 hover:text-ink-700 transition-colors"
          >
            Clear — use default
          </button>
        )}
        <p className="mt-2 text-xs text-ink-400">
          Used for buttons and accents on your lot page. Leave unset to use the ParkConfetti default.
        </p>
      </div>

      {/* Live preview */}
      <div>
        <p className="text-sm font-semibold text-ink-700 mb-3">Preview</p>
        <div
          className="rounded-2xl border border-ink-200 overflow-hidden bg-[#F5F0E8]"
          style={{ fontFamily: 'Inter, Arial, sans-serif' }}
        >
          {/* Mini form header */}
          <div className="flex flex-col items-center py-5 px-4 gap-2 bg-white border-b border-ink-200">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Your logo"
                className="h-10 w-auto max-w-[140px] object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <Logo size={26} showWordmark />
            )}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
              <MapPin className="h-3.5 w-3.5" />
              <span>{displayName}</span>
            </div>
          </div>

          {/* Tab strip */}
          <div className="flex gap-3 px-4 pt-3 pb-1">
            <span
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ backgroundColor: accentColor }}
            >
              Pay Once
            </span>
            <span className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-500 bg-gray-100">
              Pay Monthly
            </span>
          </div>

          {/* Mini form fields */}
          <div className="px-4 pt-2 pb-4 space-y-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
              <Car className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs text-gray-400">License plate</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg">
              <Phone className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs text-gray-400">Phone number</span>
            </div>
            <button
              type="button"
              className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-opacity"
              style={{ backgroundColor: accentColor }}
            >
              Continue to Payment
            </button>
          </div>

          {/* Powered by pill */}
          {logoUrl && (
            <div className="flex justify-center py-3 border-t border-ink-200 bg-white">
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full border border-ink-200 bg-cream-50 text-[10px] text-ink-400">
                <span>Powered by</span>
                <Logo size={12} showWordmark={false} />
                <span className="font-medium">Park Confetti</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
